import React, { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, Check, Clock, Users, MapPin } from "lucide-react";
import { BOAT_EXCURSIONS, buildWaLink } from "../data/site";
import { useLang, loc, fmtRs } from "../data/i18n";
import { Waves, Palm } from "./Decor";
import { BrandSymbol } from "./BrandLogo";

export default function BoatDetail({ open, onClose }) {
    const [activeId, setActiveId] = useState(BOAT_EXCURSIONS[0].id);
    const { t, lang } = useLang();

    useEffect(() => {
        if (!open) return;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    const active = BOAT_EXCURSIONS.find((e) => e.id === activeId);

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[70] bg-ink-900/80 backdrop-blur-sm flex items-start md:items-center justify-center overflow-y-auto p-0 md:p-6"
                    onClick={onClose}
                    data-testid="boat-detail-overlay"
                >
                    <motion.div
                        initial={{ y: 40, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 40, opacity: 0 }}
                        transition={{ duration: 0.4, ease: "easeOut" }}
                        className="relative bg-cream w-full max-w-6xl min-h-[80vh] md:min-h-0 md:max-h-[92vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="boat-detail-modal"
                    >
                        {/* Close */}
                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 z-20 w-11 h-11 bg-ocean text-cream hover:bg-coral flex items-center justify-center transition-colors"
                            aria-label={t.boat.close}
                            data-testid="boat-detail-close"
                        >
                            <X size={20} />
                        </button>

                        {/* Header */}
                        <div className="relative bg-ocean text-cream px-6 md:px-12 pt-10 pb-8 overflow-hidden">
                            <Palm className="absolute -bottom-6 -right-6 w-64 h-64 opacity-10" color="#77CABA" />
                            <p className="text-[11px] uppercase tracking-[0.3em] text-lagoon font-bold mb-4 flex items-center gap-3">
                                <span className="inline-block w-8 h-px bg-lagoon" />
                                {t.boat.modalKicker}
                            </p>
                            <h2 className="font-serif text-4xl md:text-6xl leading-[0.95]">
                                {t.boat.modalTitleA}{" "}
                                <em className="coral-italic">{t.boat.modalTitleB}</em>,
                                <br />
                                {t.boat.modalTitleC}
                            </h2>
                        </div>

                        {/* Tabs */}
                        <div className="sticky top-0 z-10 bg-cream border-b border-ocean/15">
                            <div className="flex overflow-x-auto no-scrollbar">
                                {BOAT_EXCURSIONS.map((e) => (
                                    <button
                                        key={e.id}
                                        onClick={() => setActiveId(e.id)}
                                        className={`whitespace-nowrap px-5 md:px-8 py-5 text-xs md:text-sm uppercase tracking-[0.18em] font-bold border-b-2 transition-colors ${
                                            activeId === e.id
                                                ? "border-coral text-coral bg-cream-200"
                                                : "border-transparent text-ink/70 hover:text-ocean"
                                        }`}
                                        data-testid={`boat-tab-${e.id}`}
                                    >
                                        {loc(e, lang).name}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Body */}
                        <div className="px-6 md:px-12 py-10">
                            <ExcursionPanel excursion={active} onClose={onClose} />
                        </div>

                        <Waves className="w-full h-10" color="#1B6CA8" />
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

// ---------------------------------------------------------------------------
// Single excursion panel — hero image, program, dynamic price calculator
// Exported so it can be reused on dedicated /excursion-*-maurice routes.
// ---------------------------------------------------------------------------
export function ExcursionPanel({ excursion, onClose, isStandalone = false }) {
    const { t, lang } = useLang();
    const exc = loc(excursion, lang);
    const [pax, setPax] = useState(exc.includedPax);
    const [lunchAdded, setLunchAdded] = useState(false);
    const [premium, setPremium] = useState(false);

    // Reset when switching tabs
    useEffect(() => {
        setPax(exc.includedPax);
        setLunchAdded(false);
        setPremium(false);
    }, [exc.id, exc.includedPax]);

    const total = useMemo(() => {
        let tot = exc.basePrice;
        if (exc.extraPerPax != null && pax > exc.includedPax) {
            tot += (pax - exc.includedPax) * exc.extraPerPax;
        }
        // Optional paid lunch (Sortie de pêche)
        if (!exc.lunchIncluded && lunchAdded && exc.lunchOptionPrice) {
            tot += pax * exc.lunchOptionPrice;
        }
        // Premium upgrade — supplement per pax (both 5 Îles & Pêche)
        if (premium && exc.premiumSupplement) {
            tot += pax * exc.premiumSupplement;
        }
        return tot;
    }, [exc, pax, lunchAdded, premium]);

    const min = exc.includedPax;
    const max = 20;

    const canChangePax = exc.extraPerPax != null;

    const summary = useMemo(() => {
        const lines = [t.boat.waIntro(exc.name), t.boat.waPax(pax)];
        if (exc.lunchIncluded) {
            lines.push(
                premium
                    ? t.boat.waLunchPremium
                    : t.boat.waLunchClassicIncluded,
            );
        } else if (!exc.lunchIncluded && exc.lunchOptionPrice) {
            if (lunchAdded) {
                lines.push(
                    premium ? t.boat.waLunchPremium : t.boat.waLunchClassic,
                );
            } else {
                lines.push(t.boat.waLunchNo);
            }
        }
        lines.push(t.boat.waTotal(fmtRs(total, lang)));
        return lines.join("\n");
    }, [exc, pax, lunchAdded, premium, total, t, lang]);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            {/* Left — visuals + program */}
            <div className="lg:col-span-7">
                <div className="relative aspect-[16/10] overflow-hidden mb-8 rounded-xl">
                    <img
                        src={exc.image}
                        alt={exc.imageAlt || exc.name}
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-ocean-900/60 to-transparent" />
                    <div className="absolute bottom-6 left-6 right-6">
                        <p className="text-[10px] uppercase tracking-[0.3em] text-lagoon font-bold mb-2">
                            {exc.tagline}
                        </p>
                        {isStandalone ? (
                            <h1 className="font-serif text-cream text-3xl md:text-4xl leading-tight">
                                {exc.name}
                            </h1>
                        ) : (
                            <p className="font-serif text-cream text-3xl md:text-4xl leading-tight">
                                {exc.name}
                            </p>
                        )}
                    </div>
                </div>

                {/* Price banner */}
                {exc.priceBanner && (
                    <div className="mb-8 p-5 md:p-6 bg-coral-50 border-l-4 border-coral rounded-r-xl">
                        <p className="text-ocean text-base md:text-lg font-semibold leading-relaxed">
                            {exc.priceBanner}
                        </p>
                    </div>
                )}
                {exc.catchPhrases && exc.catchPhrases.length > 0 && (
                    <ul className="mb-8 space-y-2">
                        {exc.catchPhrases.map((phrase, i) => (
                            <li
                                key={i}
                                className="flex gap-3 text-ink/85 text-sm md:text-base leading-relaxed"
                            >
                                <span className="text-coral font-bold">✦</span>
                                <span>{phrase}</span>
                            </li>
                        ))}
                    </ul>
                )}

                {/* Quick facts */}
                <div className="flex flex-wrap gap-6 mb-8 pb-8 border-b border-ocean/15">
                    {exc.departure && (
                        <Fact icon={Clock} label={t.boat.factDeparture} value={exc.departure} />
                    )}
                    {exc.duration && (
                        <Fact icon={Clock} label={t.boat.factDuration} value={exc.duration} />
                    )}
                    {exc.endsAt && (
                        <Fact icon={Clock} label={t.boat.factEnd} value={exc.endsAt} />
                    )}
                    <Fact
                        icon={Users}
                        label={t.boat.factBase}
                        value={`${exc.includedPax} ${t.boat.pax}`}
                    />
                </div>

                {/* SEO H2 for 5 iles */}
                {exc.id === "5iles" && (
                    <div className="mb-8">
                        <h2 className="font-serif text-2xl md:text-3xl text-ocean mb-3">
                            {t.boat.seo5ilesH2a}
                        </h2>
                        <p className="text-ink/80 text-sm md:text-base leading-relaxed">
                            {t.boat.seo5ilesPa}
                        </p>
                    </div>
                )}

                {/* Program */}
                <h2 className="font-serif text-2xl md:text-3xl text-ocean mb-5 flex items-center gap-3">
                    <MapPin className="text-coral" size={22} strokeWidth={1.5} />
                    {t.boat.yourDay}
                </h2>
                <ol className="space-y-3">
                    {exc.program.map((step, i) => (
                        <li
                            key={i}
                            className="flex gap-4 text-ink/80 text-sm leading-relaxed"
                        >
                            <span className="flex-shrink-0 w-6 h-6 bg-lagoon text-ocean text-xs font-bold flex items-center justify-center mt-0.5 rounded">
                                {i + 1}
                            </span>
                            <span>{step}</span>
                        </li>
                    ))}
                </ol>

                {/* SEO H2 secondary for 5 iles — Sud-Est */}
                {exc.id === "5iles" && (
                    <div className="mt-10 pt-8 border-t border-ocean/10">
                        <h2 className="font-serif text-2xl md:text-3xl text-ocean mb-3">
                            {t.boat.seo5ilesH2b}
                        </h2>
                        <p className="text-ink/80 text-sm md:text-base leading-relaxed">
                            {t.boat.seo5ilesPb}
                        </p>
                    </div>
                )}

                {/* Drinks (5 iles only) */}
                {exc.drinks && (
                    <div className="mt-8 p-5 bg-lagoon-50 border-l-4 border-lagoon">
                        <p className="text-[10px] uppercase tracking-[0.3em] text-ocean font-bold mb-2">
                            {t.boat.drinksLabel}
                        </p>
                        <p className="text-ink/80 text-sm leading-relaxed">
                            {exc.drinks}
                        </p>
                    </div>
                )}
            </div>

            {/* Right — Calculator */}
            <div className="lg:col-span-5">
                <div className="lg:sticky lg:top-24 bg-white border border-ocean/15 shadow-[0_10px_40px_rgba(15,42,63,0.08)]">
                    <div className="p-6 md:p-8 border-b border-ocean/10 bg-ocean text-cream">
                        <p className="text-[10px] uppercase tracking-[0.3em] text-lagoon font-bold mb-2">
                            {t.boat.calcKicker}
                        </p>
                        <p className="font-serif text-2xl">{t.boat.calcTitle}</p>
                    </div>

                    <div className="p-6 md:p-8 space-y-6">
                        {/* Pax stepper */}
                        <div>
                            <div className="flex items-center justify-between mb-3">
                                <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-ocean">
                                    {t.boat.paxLabel}
                                </label>
                                <span className="text-xs text-ink/50">
                                    {t.boat.baseIncluded(exc.includedPax)}
                                </span>
                            </div>
                            <div className="flex items-center border-2 border-ocean/15">
                                <button
                                    type="button"
                                    onClick={() =>
                                        canChangePax &&
                                        setPax((p) => Math.max(min, p - 1))
                                    }
                                    disabled={!canChangePax || pax <= min}
                                    className="w-14 h-14 flex items-center justify-center text-ocean hover:bg-cream disabled:opacity-30 disabled:cursor-not-allowed"
                                    data-testid="pax-decrement"
                                    aria-label={t.boat.removeOne}
                                >
                                    <Minus size={18} />
                                </button>
                                <input
                                    type="number"
                                    value={pax}
                                    onChange={(e) => {
                                        if (!canChangePax) return;
                                        const v = parseInt(e.target.value, 10);
                                        if (isNaN(v)) return;
                                        setPax(Math.min(max, Math.max(min, v)));
                                    }}
                                    readOnly={!canChangePax}
                                    className="flex-1 h-14 text-center font-serif text-3xl text-ocean bg-transparent outline-none"
                                    data-testid="pax-input"
                                    min={min}
                                    max={max}
                                />
                                <button
                                    type="button"
                                    onClick={() =>
                                        canChangePax &&
                                        setPax((p) => Math.min(max, p + 1))
                                    }
                                    disabled={!canChangePax || pax >= max}
                                    className="w-14 h-14 flex items-center justify-center text-ocean hover:bg-cream disabled:opacity-30 disabled:cursor-not-allowed"
                                    data-testid="pax-increment"
                                    aria-label={t.boat.addOne}
                                >
                                    <Plus size={18} />
                                </button>
                            </div>
                            {exc.extraPerPax != null && (
                                <p className="text-xs text-ink/60 mt-2">
                                    {t.boat.extraNote(
                                        fmtRs(exc.extraPerPax, lang),
                                        exc.includedPax,
                                    )}
                                </p>
                            )}
                        </div>

                        {/* Lunch toggle (only when lunch is optional) */}
                        {!exc.lunchIncluded && exc.lunchOptionPrice && (
                            <ToggleField
                                testid="lunch-toggle"
                                checked={lunchAdded}
                                onChange={setLunchAdded}
                                title={t.boat.addLunch}
                                subtitle={`+Rs ${fmtRs(exc.lunchOptionPrice, lang)} ${t.boat.perPerson}`}
                            />
                        )}

                        {/* Premium package (only when meal exists in flow) */}
                        {(exc.lunchIncluded || (lunchAdded && exc.premiumSupplement)) && (
                            <div className="space-y-3">
                                <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-ocean block">
                                    {t.boat.lunchPackage}
                                </label>
                                <div className="grid grid-cols-2 gap-2">
                                    <PackagePill
                                        active={!premium}
                                        onClick={() => setPremium(false)}
                                        title="Classic"
                                        note={t.boat.included}
                                        testid="package-classic"
                                    />
                                    <PackagePill
                                        active={premium}
                                        onClick={() => setPremium(true)}
                                        title="Premium"
                                        note={`+Rs ${fmtRs(exc.premiumSupplement, lang)}/${t.boat.pax}`}
                                        testid="package-premium"
                                    />
                                </div>
                                <p className="text-xs text-ink/60 leading-relaxed">
                                    {premium
                                        ? t.boat.premiumDesc
                                        : t.boat.classicDesc}
                                </p>
                            </div>
                        )}

                        {/* Total */}
                        <div className="pt-6 border-t border-ocean/10">
                            <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-coral mb-2">
                                {t.boat.totalLabel}
                            </p>
                            <p
                                className="font-serif text-5xl text-ocean leading-none"
                                data-testid="excursion-total"
                            >
                                Rs {fmtRs(total, lang)}
                            </p>
                            <p className="text-xs text-ink/50 mt-2">
                                {t.boat.totalDisclaimer}
                            </p>
                        </div>

                        <a
                            href={buildWaLink(summary)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={onClose}
                            className="btn-coral w-full justify-center"
                            data-testid="excursion-cta"
                        >
                            {t.boat.cta}
                            <span>→</span>
                        </a>
                        <p className="text-center text-[10px] text-ink/50 uppercase tracking-[0.2em]">
                            {t.boat.redirectNote}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Fact({ icon: Icon, label, value }) {
    return (
        <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-lagoon/20 text-ocean flex items-center justify-center">
                <Icon size={18} strokeWidth={1.5} />
            </div>
            <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-ink/50 font-bold">
                    {label}
                </p>
                <p className="font-serif text-ocean text-lg">{value}</p>
            </div>
        </div>
    );
}

function ToggleField({ checked, onChange, title, subtitle, testid }) {
    return (
        <button
            type="button"
            onClick={() => onChange(!checked)}
            className={`w-full flex items-center justify-between p-4 border-2 transition-colors text-left ${
                checked
                    ? "border-coral bg-coral-50"
                    : "border-ocean/15 bg-cream/40"
            }`}
            data-testid={testid}
            aria-pressed={checked}
        >
            <div>
                <p className="font-serif text-ocean text-lg leading-tight">
                    {title}
                </p>
                <p className="text-xs text-ink/60">{subtitle}</p>
            </div>
            <span
                className={`relative w-12 h-7 rounded-full transition-colors flex-shrink-0 ${
                    checked ? "bg-coral" : "bg-ocean/20"
                }`}
            >
                <span
                    className={`absolute top-1 h-5 w-5 bg-cream rounded-full transition-transform ${
                        checked ? "translate-x-6" : "translate-x-1"
                    }`}
                />
            </span>
        </button>
    );
}

function PackagePill({ active, onClick, title, note, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`p-3 border-2 text-left transition-colors ${
                active
                    ? "border-coral bg-coral text-cream"
                    : "border-ocean/15 bg-cream/40 text-ink hover:border-ocean/40"
            }`}
            data-testid={testid}
        >
            <p className="font-serif text-lg leading-tight flex items-center gap-2">
                {active && <Check size={16} strokeWidth={2.5} />}
                {title}
            </p>
            <p
                className={`text-xs ${
                    active ? "text-cream/85" : "text-ink/60"
                }`}
            >
                {note}
            </p>
        </button>
    );
}
